/**
 * PremiumDialog — modal overlay with Premium plans content.
 *
 * Triggered by usePremiumDialog().openDialog() from any screen.
 * The /premium page remains for direct navigation; this dialog provides
 * a low-friction path from gated features without leaving the current view.
 */

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { usePremiumDialog } from '../context/PremiumDialogContext.js';
import {
  AlreadyPremiumError,
  formatMonthlyPrice,
  usePremiumPlan,
  useStartCheckout,
} from '../hooks/useBilling.js';
import { useEntitlement } from '../hooks/useEntitlement.js';

function daysUntil(isoDate: string): number {
  return Math.max(0, Math.ceil((new Date(isoDate).getTime() - Date.now()) / 86_400_000));
}

export function PremiumDialog() {
  const { isOpen, closeDialog } = usePremiumDialog();
  const { t } = useTranslation('paywall');
  const benefitsRaw = t('benefits', { returnObjects: true });
  const benefits = Array.isArray(benefitsRaw) ? (benefitsRaw as string[]) : [];

  const { data: entitlement, isLoading: entitlementLoading } = useEntitlement();
  const { data: plan } = usePremiumPlan();
  const checkout = useStartCheckout();
  const dialogRef = useRef<HTMLDialogElement>(null);

  const isPremium = entitlement?.isPremium ?? false;
  const inTrial = entitlement?.inTrial ?? false;
  const trialEligible = entitlement?.trialEligibility === 'eligible';
  const price = formatMonthlyPrice(plan);
  const alreadyPremium = checkout.error instanceof AlreadyPremiumError;
  const trialDays = plan?.trialDays ?? 0;
  const trialDaysLeft =
    inTrial && entitlement?.currentPeriodEnd ? daysUntil(entitlement.currentPeriodEnd) : null;

  const ctaLabel =
    trialEligible && trialDays > 0
      ? `Start your ${trialDays}-day free trial`
      : price
        ? `Upgrade to Premium — ${price}`
        : 'Upgrade to Premium';

  // Open / close the native dialog
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (isOpen) dialog.showModal();
    else dialog.close();
  }, [isOpen]);

  // Sync context when the dialog is closed via Esc key
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    dialog.addEventListener('close', closeDialog);
    return () => dialog.removeEventListener('close', closeDialog);
  }, [closeDialog]);

  return (
    <dialog
      ref={dialogRef}
      className="m-auto max-h-[90dvh] w-full max-w-sm overflow-y-auto rounded-2xl border
                 border-husk bg-paper p-0 shadow-xl
                 backdrop:bg-black/40
                 dark:border-ink/20 dark:bg-shell"
      aria-label={t('title')}
    >
      <div className="flex flex-col gap-6 p-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-ink">{t('title')}</h2>
            <p className="mt-0.5 text-sm text-ink-soft">
              {isPremium && !inTrial ? t('subtitlePremium') : t('subtitleTrial')}
            </p>
          </div>
          <button
            type="button"
            onClick={closeDialog}
            aria-label="Close"
            className="shrink-0 rounded-lg p-1 text-ink-soft hover:bg-husk/50 hover:text-ink
                       focus:outline-none focus:ring-2 focus:ring-leaf dark:hover:bg-ink/10"
          >
            ✕
          </button>
        </div>

        {/* Benefits list */}
        <ul className="flex flex-col gap-2" aria-label="Premium benefits">
          {benefits.map((benefit) => (
            <li key={benefit} className="flex items-start gap-2.5 text-sm text-ink">
              <span className="mt-0.5 shrink-0 text-leaf" aria-hidden>
                ✓
              </span>
              {benefit}
            </li>
          ))}
        </ul>

        {/* State-specific content */}
        {entitlementLoading ? (
          <p className="text-sm text-ink-soft">Loading…</p>
        ) : isPremium ? (
          <div className="flex flex-col gap-3">
            {inTrial ? (
              <div className="rounded-xl border border-mist/20 bg-mist/10 px-4 py-3">
                <p className="text-sm font-semibold text-leaf">{t('trialActive')}</p>
                {trialDaysLeft !== null && (
                  <p className="mt-0.5 text-xs text-leaf">
                    {t('trialDaysLeft', { count: trialDaysLeft })}
                  </p>
                )}
              </div>
            ) : (
              <div className="rounded-xl border border-leaf/20 bg-leaf/10 px-4 py-3">
                <p className="text-sm font-semibold text-leaf">{t('alreadyPremium')}</p>
                {entitlement?.currentPeriodEnd && (
                  <p className="mt-0.5 text-xs text-leaf">
                    {entitlement.willRenew
                      ? `Renews ${new Date(entitlement.currentPeriodEnd).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}`
                      : `Expires ${new Date(entitlement.currentPeriodEnd).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}`}
                  </p>
                )}
                {entitlement?.inGracePeriod && (
                  <p className="mt-1 text-xs text-clay">{t('gracePeriod')}</p>
                )}
              </div>
            )}

            {entitlement?.managementUrl && (
              <a
                href={entitlement.managementUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-block rounded-xl border border-husk px-4 py-2 text-sm font-medium
                           text-ink hover:bg-husk/50 focus:outline-none focus:ring-2 focus:ring-leaf"
              >
                {t('manageSubscription')}
              </a>
            )}

            <button
              type="button"
              onClick={closeDialog}
              className="text-sm text-ink-soft hover:text-ink focus:outline-none"
            >
              {t('done')}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={() => checkout.mutate({})}
              disabled={checkout.isPending}
              className="rounded-xl bg-leaf px-5 py-3 text-sm font-semibold text-white
                         hover:bg-leaf-deep focus:outline-none focus:ring-2 focus:ring-leaf
                         focus:ring-offset-2 disabled:opacity-60"
            >
              {checkout.isPending ? 'Starting checkout…' : ctaLabel}
            </button>

            {trialEligible && trialDays > 0 && (
              <p className="text-xs text-ink-soft">{t('trial.subtitle')}</p>
            )}

            {alreadyPremium && (
              <p className="text-xs text-ink-soft" role="status">
                You&apos;re already on Premium.{' '}
                <Link to="/settings#plan" onClick={closeDialog} className="underline">
                  Manage subscription
                </Link>
              </p>
            )}

            {checkout.isError && !alreadyPremium && (
              <p className="text-xs text-clay" role="alert">
                Could not start checkout. Please try again.
              </p>
            )}

            <button
              type="button"
              onClick={closeDialog}
              className="text-sm text-ink-soft hover:text-ink focus:outline-none"
            >
              {t('dismiss')}
            </button>
          </div>
        )}
      </div>
    </dialog>
  );
}
